import { Injectable } from '@angular/core';
import { AppService } from '@nk/access-block';
import { ApplicationApiClient } from '@nk/api-client-lib';
import { CachingService } from '@nk/caching-lib';
import { GeoCountryEntity } from '@nk/data-entities-lib';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable()
export class MembershipGeoCountryService {
  private _appMembershipGeoCountryPreferenceSubject = new BehaviorSubject<GeoCountryEntity[]>([]);
  public appMembershipGeoCountryPreference$ = this._appMembershipGeoCountryPreferenceSubject.asObservable();

  constructor(
    private _api: ApplicationApiClient,
    private _cachingService: CachingService,
    private _appService: AppService,
  ) {}

  public geoCountryListByMembershipCache = this._cachingService.create(this, this.fetchGeoCountryListByMembership);
  public fetchGeoCountryListByMembership(): Observable<GeoCountryEntity[]> {
    return this._appService.identityDataCache.get().pipe(
      switchMap((identity) => {
        return this._api.get<GeoCountryEntity[]>(
          `app-membership/${identity.app_membership.uuid}/geo-country`,
          {},
          null,
        );
      }),
      map((geoCountryPreference) => {
        this.setGeoCountryPreference(geoCountryPreference);
        return geoCountryPreference;
      }),
    );
  }

  public removeGeoCountryFocusByAppMembership(
    appMembershipUuid: string,
    geoCountryUuid: string,
  ): Observable<GeoCountryEntity> {
    return this._api
      .delete<GeoCountryEntity>(`app-membership/${appMembershipUuid}/geo-country/${geoCountryUuid}`, {}, null)
      .pipe(
        map((geoCountryPreference) => {
          this.removeGeoCountryPreference(geoCountryPreference);
          return geoCountryPreference;
        }),
      );
  }

  public postGeoCountryByAppMembership(
    appMembershipUuid: string,
    geoCountryUuid: string,
  ): Observable<GeoCountryEntity> {
    return this._api
      .post<GeoCountryEntity>(
        `app-membership/${appMembershipUuid}/geo-country`,
        { geo_country_uuid: geoCountryUuid },
        {},
        null,
      )
      .pipe(
        map((geoCountryPreference) => {
          this.addToGeoCountryPreference(geoCountryPreference);
          return geoCountryPreference;
        }),
      );
  }

  public addToGeoCountryPreference(geoCountryPreference: GeoCountryEntity): void {
    const currentValue = this._appMembershipGeoCountryPreferenceSubject.value;
    const updatedValue = [...currentValue, geoCountryPreference];
    this._appMembershipGeoCountryPreferenceSubject.next(updatedValue);
  }

  public setGeoCountryPreference(geoCountryPreference: GeoCountryEntity[]): void {
    this._appMembershipGeoCountryPreferenceSubject.next(geoCountryPreference);
  }

  public removeGeoCountryPreference(geoCountryPreference: GeoCountryEntity): void {
    const currentValue = this._appMembershipGeoCountryPreferenceSubject.value;
    const updatedValue = currentValue.filter((geo) => geo.uuid !== geoCountryPreference.uuid);
    this._appMembershipGeoCountryPreferenceSubject.next(updatedValue);
  }
}
